import React, { useState, useContext, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, Outlet, useParams } from 'react-router-dom';
import DashboardAnual from './pages/DashboardAnualPage';
import Login from './pages/LoginPage';
import Logout from './pages/LogoutPage';
import SidebarMenu from './components/SidebarMenu';
import Header from './components/Header';
import AppContext from './components/AppContext';
import useAuth from './hooks/useAuth';
import DashboardMensual from './pages/DashboardMensualPage';
import DetallMoviment from './pages/DetallMovimentPage';
import DetallConcepteMoviment from './pages/DetallConcepteMovimentPage';
import SubdividirMovimentPage from './pages/SubdividirMovimentPage';
import DashboardConcepteAnual from './pages/DashboardConcepteAnualPage';
import DashboardConcepteMensual from './pages/DashboardConcepteMensualPage';
import TractamentManual from './pages/TractamentManualPage';
import SincroBancaria from './pages/SincroBancariaPage';
import DashboardGlobal from './pages/DashboardGlobalPage';
import DashboardConcepteGlobal from './pages/DashboardConcepteGlobalPage';
import DashboardMensualGlobal from './pages/DashboardMensualGlobalPage';
import DashboardTipusMoviment from './pages/DashboardTipusMovimentPage';
import DashboardInversioGlobal from './pages/DashboardInversioGlobalPage';
import DashboardInversioAnual from './pages/DashboardInversioAnualPage';
import DashboardInversioMensual from './pages/DashboardInversioMensualPage';
import AuthHandler from './components/AuthHandler';
import CercadorMovimentsGlobalPage from './pages/CercadorMovimentsGlobalPage';
import TestLogin from './pages/TestLoginPage';
import SincroBancariaDetallPage from './pages/SincroBancariaDetallPage';
import SincroInversions from './pages/SincroInversionsPage';
import SincroConfigPage from './pages/SincroConfigPage';
import GestioDadesPage from './pages/GestioDadesPage';
import DashboardJerarquicPage from './pages/DashboardJerarquicPage';
import DashboardTransversalPage from './pages/DashboardTransversalPage';
import RefinamentJerarquicPage from './pages/RefinamentJerarquicPage';

const PrivateRoute = ({ onSuccess }) => {
  const { userapp: paramUserapp } = useParams();
  const { ctxApp, setCtxApp } = useContext(AppContext);
  
  useEffect(() => {
    if (ctxApp) {
      if (paramUserapp && ctxApp.userapp !== paramUserapp) {
        setCtxApp(prev => ({ ...prev, userapp: paramUserapp }));
        localStorage.setItem('selectedUserApp', paramUserapp);
      } else if (!ctxApp.userapp) {
        const storedUserApp = localStorage.getItem('selectedUserApp');
        const defaultUserApp = storedUserApp || ctxApp.comptes[0].userapp;
        setCtxApp(prev => ({ ...prev, userapp: defaultUserApp }));
      }
    }
  }, [ctxApp, paramUserapp, setCtxApp]);

  return (
    <AuthHandler onSuccess={onSuccess}>
      <Outlet /> 
    </AuthHandler>
  );
};

const RedirectToDashboardAnual = ({logged, handleLoginSuccess}) => {
  const { userapp: paramUserapp } = useParams();
  const currentYear = new Date().getFullYear();
  const { ctxApp } = useContext(AppContext);
  const { ctxUser } = useAuth();

  const userapp = paramUserapp || ctxApp?.userapp || ctxApp?.comptes?.[0]?.userapp;

  if(ctxUser?.isAuthenticated)
    return <Navigate to={`/${userapp}/dashboard/${currentYear}`} replace />;
  else
    return <Login onSuccess={ handleLoginSuccess } />;
};

const App = () => {
  const [isMenuOpen, setMenuOpen] = useState(false);
  const { setCtxApp } = useContext(AppContext);

  const handleLoginSuccess = useCallback((ctxUser) => {
    //Inicialització ctxApp
    const comptes = ctxUser.fdvToken.comptes;
    const sortedComptes = [...comptes]
      .filter(c => c.actiu)
      .sort((a, b) => a.ordre - b.ordre);

    const storedUserApp = localStorage.getItem('selectedUserApp');
    const userapp = storedUserApp || sortedComptes[0]?.userapp;

    setCtxApp(prev => ({
      ...prev,
      comptes: sortedComptes,
      userapp: prev?.userapp || userapp
    }));
  }, [setCtxApp]);

  const { ctxUser, doLogin, doLogout } = useAuth(handleLoginSuccess);

  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === 'googleToken') {
        const newToken = event.newValue;
        if (newToken) {
          doLogin(newToken);
        } else {
          doLogout();
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [doLogin, doLogout]);

  const toggleMenu = () => {
    setMenuOpen(!isMenuOpen);
  };

  useEffect(() => {
    console.log("App FdvBanc - isAuthenticated: ", ctxUser.isAuthenticated);
  }, [ctxUser.isAuthenticated]);

  return (
    <Router>
      <div>
        <Header onToggleMenu={ toggleMenu }/>
        {ctxUser.isAuthenticated && <SidebarMenu open={isMenuOpen} onClose={() => setMenuOpen(false)} />}

        <Routes>
          <Route path="/login" element={<Login onSuccess={ handleLoginSuccess } />} />
          <Route path="/logout" element={<Logout />} />
          
          <Route path="/" element={ <PrivateRoute onSuccess={ handleLoginSuccess }/>}>
            <Route index element={ <RedirectToDashboardAnual handleLoginSuccess={handleLoginSuccess}/> } />
            <Route path="/:userapp" element = { <RedirectToDashboardAnual handleLoginSuccess={handleLoginSuccess}/> } />
            <Route path="/:userapp/dashboard" element = { <DashboardGlobal /> } />
            <Route path="/:userapp/dashboard/:anyo" element = { <DashboardAnual /> } />
            <Route path="/:userapp/dashboard/:anyo/mes/:mes" element = { <DashboardMensual /> } />
            <Route path="/:userapp/inversio" element = { <DashboardInversioGlobal /> } />
            <Route path="/:userapp/inversio/:anyo" element = { <DashboardInversioAnual /> } />
            <Route path="/:userapp/inversio/:anyo/mes/:mes" element = { <DashboardInversioMensual /> } />
            <Route path="/:userapp/dashboard/:anyo/concepte/:concepte" element = { <DashboardConcepteAnual /> } />
            <Route path="/:userapp/dashboard/:anyo/node/:nodeId" element = { <DashboardConcepteAnual /> } />
            <Route path="/:userapp/dashboard/:anyo/mes/:mes/concepte/:concepte" element = { <DashboardConcepteMensual /> } />
            <Route path="/:userapp/dashboard/:anyo/mes/:mes/node/:nodeId" element = { <DashboardConcepteMensual /> } />
            <Route path="/:userapp/dashboard/:anyo/concepte/:concepte/mes/:mes" element = { <DashboardConcepteMensual /> } />
            <Route path="/:userapp/dashboard/:anyo/node/:nodeId/mes/:mes" element = { <DashboardConcepteMensual /> } />
            <Route path="/:userapp/dashboard/concepte/:concepte" element = { <DashboardConcepteGlobal /> } />
            <Route path="/:userapp/dashboard/node/:nodeId" element = { <DashboardConcepteGlobal /> } />
            <Route path="/:userapp/dashboard/concepte/:concepte/:anyo" element = { <DashboardConcepteAnual /> } />
            <Route path="/:userapp/dashboard/node/:nodeId/:anyo" element = { <DashboardConcepteAnual /> } />
            <Route path="/:userapp/dashboard/concepte/:concepte/:anyo/mes/:mes" element = { <DashboardConcepteMensual /> } />
            <Route path="/:userapp/dashboard/node/:nodeId/:anyo/mes/:mes" element = { <DashboardConcepteMensual /> } />
            <Route path="/:userapp/dashboard/concepte/:concepte/tipusmov/:tipusMov" element = { <DashboardTipusMoviment /> } />
            <Route path="/:userapp/dashboard/mes/:mes" element = { <DashboardMensualGlobal /> } />
            <Route path="/:userapp/jerarquic" element = { <DashboardJerarquicPage /> } />
            <Route path="/:userapp/jerarquic/:anyo" element = { <DashboardJerarquicPage /> } />
            <Route path="/:userapp/transversal" element = { <DashboardTransversalPage /> } />
            <Route path="/:userapp/transversal/:anyo" element = { <DashboardTransversalPage /> } />
            <Route path="/:userapp/refinament" element = { <RefinamentJerarquicPage /> } />
            <Route path="/:userapp/refinament/:anyo" element = { <RefinamentJerarquicPage /> } />
            <Route path="/:userapp/cmovdetail/:id" element = { <DetallConcepteMoviment /> } />
            <Route path="/:userapp/subdivide/:id" element = { <SubdividirMovimentPage /> } />
            <Route path="/:userapp/movimentdetail/:id" element = { <DetallMoviment /> } />
            <Route path="/:userapp/tractamentmanual" element = { <TractamentManual /> } />
            <Route path="/:userapp/sincrobancaria" element = { <SincroBancaria /> } />
            <Route path="/:userapp/sincroconfig" element = { <SincroConfigPage /> } />
            <Route path="/sincroconfig" element = { <SincroConfigPage /> } />
            <Route path="/:userapp/configuracio" element = { <SincroConfigPage /> } />
            <Route path="/configuracio" element = { <SincroConfigPage /> } />
            <Route path="/:userapp/gestiodades" element = { <GestioDadesPage /> } />
            <Route path="/gestiodades" element = { <GestioDadesPage /> } />
            <Route path="/:userapp/sincroinversions" element = { <SincroInversions /> } />
            <Route path="/:userapp/cercadormovs" element = { <CercadorMovimentsGlobalPage /> } />
            <Route path="/testlogin" element = { <TestLogin /> } />
            <Route path="/:userapp/sincro/detall/:id" element={<SincroBancariaDetallPage />} />
            <Route path="/:userapp/sincro/detall/:id/:connectorId" element={<SincroBancariaDetallPage />} />
          </Route>
        </Routes>
        
      </div>        
    </Router>
  );
};

export default App;